import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CircleDollarSign, Mail, ShieldCheck, UsersRound } from "lucide-react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { formatCourseDate, formatCurrency } from "@/lib/date"
import { staffCustomersQueryOptions } from "@/lib/query-options"

export const Route = createFileRoute("/staff/customers")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(staffCustomersQueryOptions()),
  head: () => ({
    meta: [{ title: "Customers · Groundline staff" }],
  }),
  pendingComponent: StaffPagePending,
  component: Customers,
})

function Customers() {
  const { data } = useSuspenseQuery(staffCustomersQueryOptions())
  const totalRounds = data.customers.reduce(
    (sum, customer) => sum + customer.rounds,
    0
  )
  const totalSpend = data.customers.reduce(
    (sum, customer) => sum + customer.spendCents,
    0
  )

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <UsersRound className="size-4" />
            Player records
          </span>
          <h1 className="staff-page-title">Customers.</h1>
          <p className="staff-page-copy">
            Grouped from lead-player details on this course’s booking ledger.
          </p>
        </div>
      </header>

      <section className="metric-grid mb-6">
        <div className="metric">
          <span className="metric__label">
            Lead players
            <UsersRound />
          </span>
          <strong className="metric__value">{data.customers.length}</strong>
        </div>
        <div className="metric">
          <span className="metric__label">
            Player spots
            <ShieldCheck />
          </span>
          <strong className="metric__value">{totalRounds}</strong>
        </div>
        <div className="metric sm:col-span-2">
          <span className="metric__label">
            Lifetime demo value
            <CircleDollarSign />
          </span>
          <strong className="metric__value">
            {formatCurrency(totalSpend, data.course.currency)}
          </strong>
        </div>
      </section>

      <section className="ruled-panel overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">Synthetic customer directory</span>
            <p className="mt-1 text-xs text-muted-foreground">
              No production member data is included in this portfolio demo.
            </p>
          </div>
        </div>
        {data.customers.length === 0 ? (
          <div className="empty-state">
            <div>
              <UsersRound className="size-6" />
              <strong>No customer records</strong>
              <p className="mt-2">
                Lead players appear after the first course booking.
              </p>
            </div>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Bookings</th>
                  <th>Player spots</th>
                  <th>Last round</th>
                  <th>Demo value</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((customer) => (
                  <tr key={customer.email}>
                    <td>
                      <strong className="block">{customer.name}</strong>
                      <small className="mt-1 flex items-center gap-1.5 text-muted-foreground">
                        <Mail className="size-3" />
                        {customer.email}
                      </small>
                    </td>
                    <td>{customer.bookings}</td>
                    <td>{customer.rounds}</td>
                    <td>
                      {formatCourseDate(customer.lastPlayed, {
                        weekday: undefined,
                        month: "short",
                      })}
                    </td>
                    <td className="font-semibold">
                      {formatCurrency(
                        customer.spendCents,
                        data.course.currency
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
